/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import type { AnyAdminProject } from '@modules/project-admin-api/types'
import { getDeleteProjectMutationHook } from '@modules/project-admin-lib/hooks/delete'
import {
  useListElasticsearchProjectsQuery,
  useListObservabilityProjectsQuery,
  useListSecurityProjectsQuery,
} from '@modules/project-admin-lib/hooks/list'
import history from '@modules/utils/history'
import { projectsUrl } from '@modules/project-admin-lib/urls'
import DeleteProjectButton, {
  addDeletedProjectToast,
} from '@modules/project-components/ProjectPage/DeleteProjectButton'
import { isProjectDeleted } from '@modules/project-admin-lib/deleted'

export type Props = {
  project: AnyAdminProject
  onComplete: () => void
}

const AdminDeleteProjectButton: React.FunctionComponent<Props> = ({ project, onComplete }) => {
  const deleteProjectMutation = getDeleteProjectMutationHook(project.type)()

  return (
    <DeleteProjectButton
      project={project}
      onDelete={performDelete}
      onComplete={onComplete}
      isLoading={deleteProjectMutation.isLoading}
      isDisabled={isProjectDeleted(project)}
      useListElasticsearchProjectsQuery={useListElasticsearchProjectsQuery}
      useListObservabilityProjectsQuery={useListObservabilityProjectsQuery}
      useListSecurityProjectsQuery={useListSecurityProjectsQuery}
    />
  )

  function performDelete() {
    deleteProjectMutation.mutate(
      { id: project.id },
      {
        onSuccess: () => {
          addDeletedProjectToast()
          history.push(projectsUrl())
        },
      },
    )
  }
}

export default AdminDeleteProjectButton
